import React from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Play } from "lucide-react";
import { useActiveSession } from "@/context/ActiveSessionContext";

/**
 * Two globally-mounted dialogs for the Always-On Activity Log:
 *  - StopTaskModal        → shown when the user hits Stop on a running task
 *                            (either on the task page or via the top-right badge).
 *                            Break/Meeting/Call start IMMEDIATELY (no reason prompt).
 *                            Sign-off ends the day.
 *  - EndSegmentModal      → shown when the user hits "End" on a running non-task
 *                            segment (break/meeting/call). Asks for the reason and
 *                            auto-resumes the previously stopped task.
 *
 * Both modals live in Layout so they work regardless of which page the user is on.
 */
export default function GlobalActivityModals() {
  const {
    session, stopModalOpen, closeStopModal, endSegmentModalOpen, closeEndSegmentModal,
    notifyStarted, notifyStopped,
  } = useActiveSession();

  const [busy, setBusy] = React.useState(false);

  // --- Stop task modal state ------------------------------------------------
  const handleTaskStop = async (nextKind) => {
    if (!session || session.kind !== "task") return;
    setBusy(true);
    try {
      if (nextKind === "signoff") {
        // Signoff → stop the task first, then close everything.
        await api.post(`/tasks/${session.task_id}/timer/stop`);
        await api.post("/activity/segment/signoff");
        toast.success("Signed off — timer paused. Have a good one!");
      } else {
        // idle / break / meeting / call → let /activity/segment/start atomically
        // close the running task AND capture previous_task_id in one hop.
        // (Explicitly stopping first would lose the previous_task_id linkage.)
        await api.post("/activity/segment/start", { kind: nextKind });
        toast.success(nextKind === "idle" ? "Timer stopped" : `${nextKind[0].toUpperCase()}${nextKind.slice(1)} started`);
      }
      notifyStopped();
      closeStopModal();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  // --- End segment modal state ---------------------------------------------
  const [reason, setReason] = React.useState("");
  React.useEffect(() => { if (endSegmentModalOpen) setReason(""); }, [endSegmentModalOpen]);
  const segmentKindLabel = session && ["break", "meeting", "call"].includes(session.kind)
    ? session.kind : "activity";

  const handleSegmentEnd = async (resume) => {
    if (!session || !["break", "meeting", "call"].includes(session.kind)) return;
    if (!reason.trim()) { toast.error("Add a short reason for the timesheet"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/activity/segment/stop", {
        reason: reason.trim(),
        resume,
      });
      if (data.resumed_task_id) {
        toast.success("Resumed previous task");
      } else {
        toast.success("Idle — start any task to resume tracking");
      }
      notifyStarted();
      closeEndSegmentModal();
      setReason("");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      {/* Stop task → choose what's next */}
      <Dialog open={stopModalOpen} onOpenChange={(o) => { if (!busy && !o) closeStopModal(); }}>
        <DialogContent data-testid="global-stop-modal" className="max-w-md" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>What are you doing next?</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <Button data-testid="stop-choice-break" variant="outline" disabled={busy}
                    className="h-20 flex-col gap-1 border-amber-200 hover:bg-amber-50"
                    onClick={() => handleTaskStop("break")}>
              <span className="text-amber-700 font-medium">Break</span>
              <span className="text-[10px] text-slate-500">Tea, lunch, quick pause</span>
            </Button>
            <Button data-testid="stop-choice-meeting" variant="outline" disabled={busy}
                    className="h-20 flex-col gap-1 border-sky-200 hover:bg-sky-50"
                    onClick={() => handleTaskStop("meeting")}>
              <span className="text-sky-700 font-medium">Meeting</span>
              <span className="text-[10px] text-slate-500">Internal or client</span>
            </Button>
            <Button data-testid="stop-choice-call" variant="outline" disabled={busy}
                    className="h-20 flex-col gap-1 border-violet-200 hover:bg-violet-50"
                    onClick={() => handleTaskStop("call")}>
              <span className="text-violet-700 font-medium">Call</span>
              <span className="text-[10px] text-slate-500">Phone / voice</span>
            </Button>
            <Button data-testid="stop-choice-idle" variant="outline" disabled={busy}
                    className="h-20 flex-col gap-1 border-slate-300 hover:bg-slate-50"
                    onClick={() => handleTaskStop("idle")}>
              <span className="text-slate-700 font-medium">Just stop</span>
              <span className="text-[10px] text-slate-500">Back to Idle</span>
            </Button>
            <Button data-testid="stop-choice-signoff" variant="outline" disabled={busy}
                    className="h-20 flex-col gap-1 col-span-2 border-slate-300 hover:bg-slate-50"
                    onClick={() => handleTaskStop("signoff")}>
              <span className="text-slate-800 font-medium">Sign-off for now</span>
              <span className="text-[10px] text-slate-500">Wrap up — pauses your day</span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* End break/meeting/call → capture reason, auto-resume task */}
      <Dialog open={endSegmentModalOpen} onOpenChange={(o) => { if (!busy && !o) closeEndSegmentModal(); }}>
        <DialogContent data-testid="global-end-segment-modal" className="max-w-md" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Log this {segmentKindLabel}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Reason (required)</Label>
              <Input
                data-testid="end-segment-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={
                  segmentKindLabel === "meeting" ? "e.g. Client review call with VDM"
                    : segmentKindLabel === "call" ? "e.g. Follow-up with KMS SA"
                    : "e.g. Lunch"
                }
                autoFocus
                className="mt-1"
              />
              <p className="text-[11px] text-slate-500 mt-1">This appears on your timesheet.</p>
            </div>
            {session?.previous_task_id ? (
              <Button
                data-testid="end-segment-resume-btn"
                onClick={() => handleSegmentEnd(true)}
                disabled={busy || !reason.trim()}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
              >
                <Play className="w-3.5 h-3.5" /> Save & resume task
              </Button>
            ) : null}
            <Button
              data-testid="end-segment-idle-btn"
              onClick={() => handleSegmentEnd(false)}
              disabled={busy || !reason.trim()}
              variant="outline"
              className="w-full"
            >
              {busy ? "Saving…" : "Save & go idle"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
